import {FormConfig} from '../../../models/form-config';
import {FieldType} from '../../../models/field-type';
import {Validator} from '../../../models/validator.enum';
import {AssetsService} from '../../../services/assets.service';
import {isKIOSKPOLIS, isNestBank} from '../../../../core/utils/utils';
import {OfferPrimaryCoverageCode} from '../../../../core/consts/offer-primary-coverage-code.enum';

export class InsuranceCoveragesSectionConfig {

    offersControlPanelContext: boolean;

    iconsConfig: InsuranceCoverageIconsConfig[] = [
        {
            coverage: OfferPrimaryCoverageCode.OC,
            icon: 'oc.svg',
            nextIcon: 'next-oc-dark.svg',
            nextIconChecked: 'next-oc-light.svg',
            offersControlPanelIcon: isNestBank() ? 'next-oc-dark.svg' : 'next-oc-light.svg',
            offersControlPanelIconChecked: isNestBank() ? 'next-oc-black.svg' : 'next-oc-dark.svg'
        },
        {
            coverage: OfferPrimaryCoverageCode.NNW,
            icon: 'nnw.svg',
            nextIcon: 'next-nnw-dark.svg',
            nextIconChecked: 'next-nnw-light.svg',
            offersControlPanelIcon: isNestBank() ? 'next-nnw-dark.svg' : 'next-nnw-light.svg',
            offersControlPanelIconChecked: isNestBank() ? 'next-nnw-black.svg' : 'next-nnw-dark.svg'
        },
        {
            coverage: OfferPrimaryCoverageCode.ASSISTANCE,
            icon: 'assistance.svg',
            nextIcon: 'next-assistance-dark.svg',
            nextIconChecked: 'next-assistance-light.svg',
            offersControlPanelIcon: isNestBank() ? 'next-assistance-dark.svg' : 'next-assistance-light.svg',
            offersControlPanelIconChecked: isNestBank() ? 'next-assistance-black.svg' : 'next-assistance-dark.svg'
        },
        {
            coverage: OfferPrimaryCoverageCode.AC,
            icon: 'ac.svg',
            nextIcon: 'next-ac-dark.svg',
            nextIconChecked: 'next-ac-light.svg',
            offersControlPanelIcon: isNestBank() ? 'next-ac-dark.svg' : 'next-ac-light.svg',
            offersControlPanelIconChecked: isNestBank() ? 'next-ac-black.svg' : 'next-ac-dark.svg'
        },
        {
            coverage: OfferPrimaryCoverageCode.AC_MINI,
            icon: 'ac.svg',
            nextIcon: 'next-ac-dark.svg',
            nextIconChecked: 'next-ac-light.svg',
            offersControlPanelIcon: isNestBank() ? 'next-ac-dark.svg' : 'next-ac-light.svg',
            offersControlPanelIconChecked: isNestBank() ? 'next-ac-black.svg' : 'next-ac-dark.svg'
        }
    ];

    get nextInsuranceCoverages(): boolean {
        return isKIOSKPOLIS();
    }

    get iconsDirectoryPath(): string {
        return this.assetsService.getIconsDirectoryPath();
    }

    constructor(private assetsService: AssetsService) { }

    createInsuranceCoveragesConfig(offersControlPanelContext: boolean): FormConfig {
        this.offersControlPanelContext = offersControlPanelContext;
        let formConfig = <FormConfig>{
            formKey: 'INSURANCE_COVERAGES_SECTION',
            fieldsConfig: [
                {
                    fieldKey: OfferPrimaryCoverageCode.OC,
                    caption: 'INSURANCE_COVERAGES_SECTION_COMPONENT.FORM_FIELDS.oc',
                    type: FieldType.Checkbox,
                    validators: [Validator.REQUIRED],
                    checkboxConfig: {
                        icon: this.getCheckboxIcon(OfferPrimaryCoverageCode.OC),
                        iconChecked: this.getCheckboxIconChecked(OfferPrimaryCoverageCode.OC),
                        disableVisualChangesOnDisabled: !offersControlPanelContext,
                        clickableLabel: true
                    }
                },
                {
                    fieldKey: OfferPrimaryCoverageCode.NNW,
                    caption: 'INSURANCE_COVERAGES_SECTION_COMPONENT.FORM_FIELDS.nnw',
                    type: FieldType.Checkbox,
                    checkboxConfig: {
                        icon: this.getCheckboxIcon(OfferPrimaryCoverageCode.NNW),
                        iconChecked: this.getCheckboxIconChecked(OfferPrimaryCoverageCode.NNW),
                        clickableLabel: true
                    }
                },
                {
                    fieldKey: OfferPrimaryCoverageCode.ASSISTANCE,
                    caption: 'INSURANCE_COVERAGES_SECTION_COMPONENT.FORM_FIELDS.assistance',
                    type: FieldType.Checkbox,
                    checkboxConfig: {
                        icon: this.getCheckboxIcon(OfferPrimaryCoverageCode.ASSISTANCE),
                        iconChecked: this.getCheckboxIconChecked(OfferPrimaryCoverageCode.ASSISTANCE),
                        clickableLabel: true
                    }
                },
                {
                    fieldKey: OfferPrimaryCoverageCode.AC,
                    caption: 'INSURANCE_COVERAGES_SECTION_COMPONENT.FORM_FIELDS.ac',
                    type: FieldType.Checkbox,
                    checkboxConfig: {
                        icon: this.getCheckboxIcon(OfferPrimaryCoverageCode.AC),
                        iconChecked: this.getCheckboxIconChecked(OfferPrimaryCoverageCode.AC),
                        clickableLabel: true
                    }
                },
                {
                    fieldKey: OfferPrimaryCoverageCode.AC_MINI,
                    caption: 'INSURANCE_COVERAGES_SECTION_COMPONENT.FORM_FIELDS.acMini',
                    type: FieldType.Checkbox,
                    checkboxConfig: {
                        icon: this.getCheckboxIcon(OfferPrimaryCoverageCode.AC_MINI),
                        iconChecked: this.getCheckboxIconChecked(OfferPrimaryCoverageCode.AC_MINI),
                        clickableLabel: true
                    }
                },
            ]
        };
        return formConfig;
    }

    private getCoverageIconsConfig(coverage: OfferPrimaryCoverageCode): InsuranceCoverageIconsConfig {
        return this.iconsConfig.find(config => config.coverage === coverage);
    }

    private getCheckboxIcon(coverage: OfferPrimaryCoverageCode): string {
        const config = this.getCoverageIconsConfig(coverage);
        let icon;
        if (this.offersControlPanelContext) {
            icon = config.offersControlPanelIcon;
        } else {
            icon = this.nextInsuranceCoverages ? config.nextIcon : config.icon;
        }
        return `${this.iconsDirectoryPath}/${icon}`;
    }

    private getCheckboxIconChecked(coverage: OfferPrimaryCoverageCode): string | null {
        const config = this.getCoverageIconsConfig(coverage);
        let icon;
        if (this.offersControlPanelContext) {
            icon = config.offersControlPanelIconChecked;
        } else {
            icon = this.nextInsuranceCoverages ? config.nextIconChecked : null;
        }
        return icon ? `${this.iconsDirectoryPath}/${icon}` : null;
    }
}

export interface InsuranceCoverageIconsConfig {
    coverage: OfferPrimaryCoverageCode;
    icon: string;
    nextIcon: string; // Moto 3.0
    nextIconChecked: string; // Moto 3.0
    offersControlPanelIcon: string; // Moto 3.0
    offersControlPanelIconChecked: string; // Moto 3.0
}
